'use client'

import { useEffect, useState, useRef } from 'react'
import { useMemberInfo } from '~/hooks/use-member-info'
import { useLoyaltySession } from '~/hooks/use-loyalty-session'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'

const USE_AUTH = process.env.NEXT_PUBLIC_USE_AUTH === 'true'

interface AuthSectionProps {
  isActive: boolean
}

export default function AuthSection({ isActive }: AuthSectionProps) {
  if (!USE_AUTH) return null
  return <AuthSectionInner isActive={isActive} />
}

function AuthSectionInner({ isActive }: AuthSectionProps) {
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const { data: memberInfo } = useMemberInfo()
  const { isLoggedIn, login, logout } = useLoyaltySession()
  const fullName: string =
    memberInfo?.member?.full_name ?? memberInfo?.data?.fullName ?? memberInfo?.data?.name ?? ''
  const [open, setOpen] = useState(false)
  const [menuWidth, setMenuWidth] = useState<number>(0)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (!isLoggedIn) {
    return (
      <button
        type="button"
        onClick={login}
        className={`font-body font-medium leading-6 transition-colors hover:underline ${
          isActive ? 'text-gray-900 hover:text-primary-600' : 'text-white hover:text-primary-100'
        }`}
      >
        {t('auth.login')}
      </button>
    )
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        ref={btnRef}
        onClick={() => {
          if (!open && btnRef.current) {
            setMenuWidth(btnRef.current.offsetWidth)
          }
          setOpen(!open)
        }}
        aria-label={fullName || t('auth.account')}
        className={`font-body flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-light leading-6 transition-all duration-300 ${
          isActive
            ? 'text-gray-900 hover:bg-gray-50 hover:text-primary-600'
            : 'text-white hover:bg-white/10 hover:text-white'
        }`}
      >
        <span className="font-medium">{fullName || t('auth.account')}</span>
        <div className={`transform transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 rounded-lg border border-gray-100 bg-white py-2 shadow-lg">
          <button
            onClick={logout}
            className="flex w-[130px] items-center gap-2 px-4 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {t('auth.logout')}
          </button>
        </div>
      )}
    </div>
  )
}
